// The canonical metrics' surfaces (spec/17 §3, §4; F-ANL-01, F-ANL-02):
//
//   GET /v1/organizations/{o}/venues/{v}/reports/overview?from&to   reports:financial   R-01, M-01 … M-22 for a venue
//   GET /v1/organizations/{o}/reports/overview?from&to              reports:financial   the organisation, by venue
//   GET /v1/organizations/{o}/venues/{v}/floor/live                  shifts:day_close    the floor's live figures
//   GET /v1/hq/live                                                  hq:organizations    HQ Live
//
// **One definition per metric** (spec/17 §1.1). Every figure here is computed by the one function in
// `@clavian/domain` that defines its M-id — the venue's overview, the organisation's comparison, the floor's "takings
// today", the day close's `revenue` and HQ Live's takings all call `revenueOf` on the same ledger cells — so two
// surfaces cannot show two revenues for one venue and day.
//
// **One unit** (spec/17 §1.2): money is a `Money` pair in minor units, time integer seconds, a ratio basis points
// (10 000 = 100 %) with its exact numerator and denominator. **Business days** (spec/17 §1.3) are the venue's, in its
// timezone with its cutoff. **Empty is not zero** (spec/17 §1.5): a metric with no facts in the range is `null`, and
// a surface shows "no data". A metric whose facts the Platform does not record yet (loyalty, reservations,
// subscriptions, commission) is listed in `notRecorded` and is never filled in.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { MemberIdSchema, OrganizationIdSchema, StationClassIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_ORGANIZATIONS, REPORTS_FINANCIAL, REPORTS_READ, SHIFTS_DAY_CLOSE } from "../permissions/index.js";
import { MoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema, LocalDateSchema, LocalTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema, VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
/** The longest range one overview reads: a quarter (as the Games played report). */
export const REPORTS_OVERVIEW_MAX_DAYS = 92;
/** At most this many titles in the overview's M-11 list. */
export const REPORTS_OVERVIEW_TOP_TITLES = 10;
/** An M-id (spec/17 §3). */
export const MetricIdSchema = z
    .string()
    .regex(/^M-\d{2}$/)
    .openapi('MetricId', { description: 'A canonical metric id from spec/17 §3, `M-01` … `M-22`.', example: 'M-01' });
/** A ratio: its exact parts and basis points, half up (10 000 = 100 %). */
export const MetricRatioSchema = z
    .object({
    numerator: z.int().min(0),
    denominator: z.int().min(1),
    basisPoints: z.int().min(0),
})
    .openapi('MetricRatio', { description: 'A ratio as its exact numerator and denominator, and in basis points (10 000 = 100 %).' });
export const ReportsRangeQuerySchema = z
    .object({
    /** The first business day, in the venue's timezone. */
    from: LocalDateSchema,
    /** The last business day, inclusive. At most 92 days after `from`. */
    to: LocalDateSchema,
})
    .strict()
    .openapi('ReportsRangeQuery');
export const MetricTenderAmountSchema = z
    .object({ tender: z.enum(['cash', 'counter_card', 'counter_upi', 'online', 'wallet']), amount: MoneySchema })
    .openapi('MetricTenderAmount', { description: 'M-01 net of one tender.' });
export const MetricCategoryAmountSchema = z
    .object({ category: z.enum(['play', 'food', 'retail', 'membership', 'other']), amount: MoneySchema })
    .openapi('MetricCategoryAmount', { description: 'M-02 net sales of one category, ex-tax.' });
/** M-01 Revenue. */
export const RevenueMetricSchema = z
    .object({
    /** Settled payments minus settled refunds. */
    total: MoneySchema,
    payments: MoneySchema,
    refunds: MoneySchema,
    /** Net by tender; wallet is wallet cash spent (never topped up), online is what the provider captured. */
    byTender: z.array(MetricTenderAmountSchema),
})
    .openapi('RevenueMetric', {
    description: 'M-01 Revenue (spec/17 §3): settled payments minus settled refunds by the settled date in the venue timezone, ' +
        'excluding on-account until settled and wallet top-ups until spent.',
});
export const MetricDayRevenueSchema = z
    .object({ date: LocalDateSchema, revenue: MoneySchema.nullable() })
    .openapi('MetricDayRevenue', { description: 'M-01 on one business day; null with no payment that day.' });
export const NetSalesMetricSchema = z
    .object({
    total: MoneySchema,
    discounts: MoneySchema,
    /** Wallet credit the venue gave away (top-up bonuses, coupon grants, manual credits): promotional contra, not a sale. */
    walletCredit: MoneySchema,
    byCategory: z.array(MetricCategoryAmountSchema),
})
    .openapi('NetSalesMetric', {
    description: 'M-02 Net sales by category (spec/17 §3): sales credits net of refunds, ex-tax. The total is the categories less discounts and less the wallet credit given, which is never counted as a (negative) category.',
});
export const MetricTaxComponentSchema = z
    .object({ component: z.string(), amount: MoneySchema })
    .openapi('MetricTaxComponent', { description: 'M-03 for one GST component.' });
export const TaxMetricSchema = z
    .object({
    total: MoneySchema,
    byComponent: z.array(MetricTaxComponentSchema),
    /**
     * Beside M-03, never in it: GST on what was paid for with money carried over from a café's previous system, where
     * GST was already paid at top-up (a migration's tax-paid balance). Absent when none.
     */
    paidAtTopUp: MoneySchema.optional(),
})
    .openapi('TaxMetric', { description: 'M-03 Tax collected (spec/17 §3): the net `tax_payable` credit by component.' });
export const DeferredRevenueMetricSchema = z
    .object({
    total: MoneySchema,
    passes: MoneySchema.nullable(),
    walletCash: MoneySchema.nullable(),
    walletBonus: MoneySchema.nullable(),
    points: MoneySchema.nullable(),
    reservationPrepayments: MoneySchema.nullable(),
})
    .openapi('DeferredRevenueMetric', { description: 'M-04 Deferred revenue / liability (spec/17 §3); a part is null where it has no data.' });
export const OrdersMetricSchema = z
    .object({ placed: z.int().min(0), paid: z.int().min(0), averageOrderValue: MoneySchema.nullable() })
    .openapi('OrdersMetric', { description: 'M-05 Orders (spec/17 §3); average order value = M-01 ÷ paid orders.' });
export const PlayerCountsMetricSchema = z
    .object({ active: z.int().min(0), new: z.int().min(0), returning: z.int().min(0) })
    .openapi('PlayerCountsMetric', { description: 'M-06 active, M-07 new and M-08 returning players (spec/17 §3).' });
export const SessionsMetricSchema = z
    .object({ sessions: z.int().min(0), playSeconds: z.int().min(0), averageSeconds: z.int().min(0) })
    .openapi('SessionsMetric', { description: 'M-09 Sessions (spec/17 §3): count, charged play seconds and the average length.' });
export const OccupancyFigureSchema = z
    .object({ inSessionSeconds: z.int().min(0), availableSeconds: z.int().min(0), ratio: MetricRatioSchema.nullable() })
    .openapi('OccupancyFigure', { description: 'Station-seconds in session of station-seconds available in open hours.' });
export const OccupancyClassFigureSchema = z
    .object({ classId: StationClassIdSchema, inSessionSeconds: z.int().min(0), availableSeconds: z.int().min(0), ratio: MetricRatioSchema.nullable() })
    .openapi('OccupancyClassFigure', { description: 'M-10 for one station class.' });
export const OccupancyMetricSchema = z
    .object({ total: OccupancyFigureSchema, byClass: z.array(OccupancyClassFigureSchema), hoursSet: z.boolean() })
    .openapi('OccupancyMetric', {
    description: 'M-10 Occupancy / utilisation (spec/17 §3), overall and by class. `hoursSet` is false for a venue with no opening ' +
        'hours recorded, whose whole range then counts as open.',
});
export const ReportTitleTotalSchema = z
    .object({ titleId: z.string(), name: z.string(), plays: z.int().min(0), seconds: z.int().min(0), players: z.int().min(0) })
    .openapi('ReportTitleTotal', { description: 'One title’s plays, capped seconds and distinct players.' });
export const TitlePlaysMetricSchema = z
    .object({ plays: z.int().min(0), seconds: z.int().min(0), players: z.int().min(0), top: z.array(ReportTitleTotalSchema).max(REPORTS_OVERVIEW_TOP_TITLES) })
    .openapi('TitlePlaysMetric', { description: 'M-11 Title plays (spec/17 §3): deduplicated, each capped at 24 h, with distinct players.' });
export const ReceivablesMetricSchema = z
    .object({ outstanding: MoneySchema, players: z.int().min(0), days0to7: MoneySchema, days8to30: MoneySchema, days31plus: MoneySchema })
    .openapi('ReceivablesMetric', { description: 'M-12 On-account outstanding (spec/17 §3), aged by when raised, oldest settled first.' });
export const CompsMetricSchema = z
    .object({ compSeconds: z.int().min(0), compValue: MoneySchema, unvaluedCompSeconds: z.int().min(0), discounts: MoneySchema })
    .openapi('CompsMetric', { description: 'M-14 Comps and discounts (spec/17 §3): comp time valued at the session’s rate, and discounts.' });
export const CallsCategoryFiguresSchema = z
    .object({
    category: z.string(),
    count: z.int().min(0),
    medianAcknowledgeSeconds: z.int().min(0).nullable(),
    medianResolveSeconds: z.int().min(0).nullable(),
})
    .openapi('CallsCategoryFigures', { description: 'M-15 for one call category.' });
export const CallsMetricSchema = z
    .object({
    count: z.int().min(0),
    medianAcknowledgeSeconds: z.int().min(0).nullable(),
    medianResolveSeconds: z.int().min(0).nullable(),
    byCategory: z.array(CallsCategoryFiguresSchema),
})
    .openapi('CallsMetric', { description: 'M-15 Calls for help (spec/17 §3): count, median to acknowledge and to resolve, by category.' });
export const CashVarianceMetricSchema = z
    .object({ total: MoneySchema, shifts: z.int().min(0), shiftsWithVariance: z.int().min(0) })
    .openapi('CashVarianceMetric', { description: 'M-16 Cash variance (spec/17 §3): counted − expected cash of the closed shifts.' });
export const WalletMetricSchema = z
    .object({
    topUps: MoneySchema,
    bonusGranted: MoneySchema,
    spent: MoneySchema,
    spentCash: MoneySchema,
    refunded: MoneySchema,
    outstandingCash: MoneySchema.nullable(),
    outstandingBonus: MoneySchema.nullable(),
})
    .openapi('WalletMetric', { description: 'M-19 Wallet (spec/17 §3): flows at the venue and the wallets’ outstanding balance now.' });
export const TurnoverLineSchema = z
    .object({ registration: z.string(), taxMode: z.enum(['regular', 'composition', 'unregistered']), turnover: MoneySchema, days: z.int().min(0) })
    .openapi('TurnoverLine', { description: 'M-22 Turnover for one GSTIN (or `venue:<id>`) under one tax mode.' });
/** R-01 and the canonical metrics for one venue and range (spec/17 §4.1). */
export const VenueReportOverviewSchema = z
    .object({
    venueId: VenueIdSchema,
    from: LocalDateSchema,
    to: LocalDateSchema,
    /** The venue's IANA timezone and business-day cutoff the days are counted in. */
    timeZone: z.string(),
    businessDayCutoff: z.string(),
    currency: z.string().length(3),
    /** M-01 over the range. */
    revenue: RevenueMetricSchema.nullable(),
    /** M-01 per business day, oldest first, every day of the range. */
    revenueByDay: z.array(MetricDayRevenueSchema),
    netSales: NetSalesMetricSchema.nullable(),
    tax: TaxMetricSchema.nullable(),
    /** M-04 as at now, organisation-wide. */
    deferred: DeferredRevenueMetricSchema.nullable(),
    orders: OrdersMetricSchema.nullable(),
    /** M-07 counts a player's first session at the organisation; M-08 compares with the prior equal range. */
    players: PlayerCountsMetricSchema.nullable(),
    /** Sessions filed under the range's business days. */
    sessions: SessionsMetricSchema.nullable(),
    occupancy: OccupancyMetricSchema.nullable(),
    titles: TitlePlaysMetricSchema.nullable(),
    /** M-12 now, organisation-wide: a player owes the organisation, not one venue. */
    receivables: ReceivablesMetricSchema.nullable(),
    /** M-13 Refund rate: refunds ÷ payments of M-01. */
    refundRate: MetricRatioSchema.nullable(),
    comps: CompsMetricSchema.nullable(),
    calls: CallsMetricSchema.nullable(),
    cashVariance: CashVarianceMetricSchema.nullable(),
    wallet: WalletMetricSchema.nullable(),
    /** M-22 by the tax registration in force each day. */
    turnover: z.array(TurnoverLineSchema).nullable(),
    /** The metrics whose facts the Platform does not record yet: empty, never filled in (spec/17 §1.5). */
    notRecorded: z.array(MetricIdSchema),
    /** True when a read reached its limit and a figure may be low; the range should be narrowed. */
    partial: z.boolean(),
    /** How many of the range's days were read from the nightly rollup, and how many live from the ledger. */
    rollupDays: z.int().min(0),
    liveDays: z.int().min(0),
})
    .openapi('VenueReportOverview', {
    description: 'The venue’s Reports overview (spec/17 R-01): the canonical metrics M-01 … M-22 for a range of business days (F-ANL-01).',
});
export const getVenueReportOverviewRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/reports/overview',
    operationId: 'getVenueReportOverview',
    tags: ['reports'],
    summary: 'Reports overview: the venue’s canonical metrics for a range',
    description: 'Revenue (M-01) with its tenders and per-day trend, net sales, tax, orders, players, sessions, occupancy, titles, ' +
        'receivables, refunds, comps and discounts, calls, cash variance, wallet and turnover — each computed by its one ' +
        'definition (spec/17 §3) over the venue’s business days `from` … `to` (at most 92). A metric with no data is null. ' +
        'Requires `reports:financial`.',
    [PERMISSION_EXTENSION]: REPORTS_FINANCIAL,
    request: { params: VenueScopeParamsSchema, query: ReportsRangeQuerySchema },
    responses: {
        200: jsonResponse(VenueReportOverviewSchema, 'The overview.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such venue in this organisation, or not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** One venue's line in the organisation's overview. */
export const OrganizationReportVenueSchema = z
    .object({
    venueId: VenueIdSchema,
    name: z.string(),
    timeZone: z.string(),
    currency: z.string().length(3),
    /** M-01 over the venue's own business days `from` … `to`. */
    revenue: MoneySchema.nullable(),
    /** M-09. */
    sessions: z.int().min(0).nullable(),
    /** M-10, in basis points. */
    occupancyBasisPoints: z.int().min(0).nullable(),
    /** M-07 at the organisation: players whose first session anywhere at it was at this venue in the range. */
    newPlayers: z.int().min(0).nullable(),
    /** M-05 placed orders. */
    orders: z.int().min(0).nullable(),
})
    .openapi('OrganizationReportVenue', { description: 'One venue’s canonical metrics in the organisation’s overview (spec/13 §15, F-ANL-01).' });
export const OrganizationReportOverviewSchema = z
    .object({
    from: LocalDateSchema,
    to: LocalDateSchema,
    /** The venues you can see, most revenue first (a venue with none last). */
    venues: z.array(OrganizationReportVenueSchema),
    /** M-01 summed across the venues, per currency (an organisation trades in one; a mixed one lists each). */
    revenue: z.array(MoneySchema),
    /** M-09 summed across the venues. */
    sessions: z.int().min(0).nullable(),
    /** M-12 now: what players owe the organisation. */
    receivablesOutstanding: MoneySchema.nullable(),
})
    .openapi('OrganizationReportOverview', {
    description: 'The organisation’s overview (spec/13 §15, spec/17 §4.2): the canonical metrics by venue, side by side (F-ANL-01).',
});
export const getOrganizationReportOverviewRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/reports/overview',
    operationId: 'getOrganizationReportOverview',
    tags: ['reports'],
    summary: 'Reports overview across the organisation’s venues',
    description: 'Each venue you work at, side by side — revenue (M-01), sessions, occupancy, new players and orders over its own ' +
        'business days `from` … `to` (at most 92) — with the revenue and sessions summed and what players owe (M-12). ' +
        'Requires `reports:financial`.',
    [PERMISSION_EXTENSION]: REPORTS_FINANCIAL,
    request: { params: OrganizationScopeParamsSchema, query: ReportsRangeQuerySchema },
    responses: {
        200: jsonResponse(OrganizationReportOverviewSchema, 'The overview.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// The Reports screen's tabs (F-ANL-03, spec/17 R-01 … R-03, R-06, R-07, R-10; spec/13 §15)
//
//   GET /v1/organizations/{o}/reports/range?preset[&venueId]            reports:read        a preset's business days
//   GET /v1/organizations/{o}/reports/revenue?from&to[&venueId]         reports:financial   Overview, Revenue, Passes & wallet
//   GET /v1/organizations/{o}/reports/sessions?from&to[&venueId]        reports:read        Overview, Sessions & occupancy
//   GET /v1/organizations/{o}/reports/products?from&to[&venueId]        reports:financial   Products
//   GET /v1/organizations/{o}/reports/staff?from&to[&venueId]           reports:financial   Staff
//
// **Scope**: one venue (`venueId`) or, without it, every venue the member works at, rolled up — each venue's figures
// over its own business days, summed. **Every report carries the previous period** (as many business days, just
// before) so a surface shows "compared with the previous period" without a second call. **Revenue** is the one
// definition (`revenueBreakdownOf`, M-01): wallet spends are revenue when spent, top-ups are money in (a liability),
// bonus spent is a promotional cost, apart.
// ---------------------------------------------------------------------------------------------
/** The named ranges a report offers (`@clavian/domain/reports` `REPORT_PRESETS`). */
export const ReportPresetSchema = z
    .enum(['today', 'yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth'])
    .openapi('ReportPreset', { description: 'A named range of business days, resolved in the venue’s timezone and cutoff.' });
export const ReportRangeQuerySchema = z
    .object({
    preset: ReportPresetSchema,
    /** The venue whose timezone and cutoff decide "today"; without it, the first venue you work at. */
    venueId: VenueIdSchema.optional(),
})
    .strict()
    .openapi('ReportRangeQuery');
export const ReportRangeSchema = z
    .object({
    preset: ReportPresetSchema,
    from: LocalDateSchema,
    to: LocalDateSchema,
    /** The period it is compared with: as many business days, just before. */
    previousFrom: LocalDateSchema,
    previousTo: LocalDateSchema,
    /** The venue's business day now. */
    today: LocalDateSchema,
    timeZone: z.string(),
    businessDayCutoff: LocalTimeSchema,
})
    .openapi('ReportRange', { description: 'A preset’s business days at the venue (its timezone and cutoff, ADR-0029), and the previous period.' });
export const ReportsScopeQuerySchema = z
    .object({
    from: LocalDateSchema,
    to: LocalDateSchema,
    /** One venue; without it, every venue you work at, rolled up. */
    venueId: VenueIdSchema.optional(),
})
    .strict()
    .openapi('ReportsScopeQuery');
export const ReportVenueRefSchema = z.object({ venueId: VenueIdSchema, name: z.string() }).openapi('ReportVenueRef');
/** What every tab's report says about itself: its days, the previous period, its currency and its venues. */
const reportScopeShape = {
    from: LocalDateSchema,
    to: LocalDateSchema,
    previousFrom: LocalDateSchema,
    previousTo: LocalDateSchema,
    currency: z.string().length(3),
    /** The venues rolled up: one with `venueId`, else every venue you work at. */
    venues: z.array(ReportVenueRefSchema),
    /** True when a read reached its limit and a figure may be low; the range should be narrowed. */
    partial: z.boolean(),
};
export const MoneyInTenderAmountSchema = z
    .object({ tender: z.enum(['cash', 'counter_card', 'counter_upi', 'online']), amount: MoneySchema })
    .openapi('MoneyInTenderAmount', { description: 'Money that came in through one outside tender, top-ups included, less what was handed back.' });
export const RevenueBreakdownSchema = z
    .object({
    /** M-01: payments − refunds, wallet spends included, top-ups and bonus excluded. */
    revenue: MoneySchema,
    payments: MoneySchema,
    refunds: MoneySchema,
    /** Revenue by how it was paid; `wallet` is wallet cash spent. */
    bySource: z.array(MetricTenderAmountSchema),
    /** Revenue paid from wallets (wallet cash spent, net of spends put back). */
    walletSpent: MoneySchema,
    /** Cash, card, UPI and online taken — top-ups included — less what was handed back. */
    moneyIn: MoneySchema,
    moneyInBySource: z.array(MoneyInTenderAmountSchema),
    /** Put into wallets: a liability until spent, never revenue. */
    walletTopUps: MoneySchema,
    walletHandedBack: MoneySchema,
    /** Bonus credit spent: a promotional cost, never revenue. */
    bonusSpent: MoneySchema,
    bonusGranted: MoneySchema,
})
    .openapi('RevenueBreakdown', {
    description: 'Revenue (M-01) and what sits beside it, from the same ledger cells: revenue = moneyIn − walletTopUps + ' +
        'walletHandedBack + walletSpent. Wallet spends are revenue when spent; a top-up is money in, not revenue; bonus ' +
        'spent is a promotional cost.',
});
export const RevenueReportDaySchema = z
    .object({
    date: LocalDateSchema,
    /** M-01 that day; null with no payment-account line. */
    revenue: MoneySchema.nullable(),
    moneyIn: MoneySchema.nullable(),
    walletSpent: MoneySchema,
    walletTopUps: MoneySchema,
    bonusSpent: MoneySchema,
})
    .openapi('RevenueReportDay', { description: 'One business day of the revenue trend (every venue in scope, each on its own business day).' });
export const RevenueReportVenueSchema = z
    .object({ venueId: VenueIdSchema, name: z.string(), revenue: MoneySchema.nullable(), moneyIn: MoneySchema.nullable(), previousRevenue: MoneySchema.nullable() })
    .openapi('RevenueReportVenue', { description: 'One venue’s revenue and money in, and its revenue over the previous period.' });
export const WalletReportSchema = z
    .object({
    topUps: MoneySchema,
    handedBack: MoneySchema,
    /** Wallet cash spent: revenue. */
    spent: MoneySchema,
    bonusGranted: MoneySchema,
    /** Bonus spent: promotional cost. */
    bonusSpent: MoneySchema,
    /** Owed to players now, organisation-wide (M-19, M-04). */
    outstandingCash: MoneySchema.nullable(),
    outstandingBonus: MoneySchema.nullable(),
    /** Unused pass time valued at its price (M-04), organisation-wide, now. */
    passLiability: MoneySchema.nullable(),
    /** What players owe on account (M-12), organisation-wide, now. */
    receivablesOutstanding: MoneySchema.nullable(),
})
    .openapi('WalletReport', { description: 'Wallet flows over the range, and what the organisation owes players (wallets, passes) and is owed now.' });
export const RevenueReportSchema = z
    .object({
    ...reportScopeShape,
    /** The range's revenue breakdown; null with no payment-account or wallet line. */
    totals: RevenueBreakdownSchema.nullable(),
    previous: RevenueBreakdownSchema.nullable(),
    /** Every business day of the range, oldest first. */
    byDay: z.array(RevenueReportDaySchema),
    /** M-02 by category, ex-tax, less discounts. */
    netSales: NetSalesMetricSchema.nullable(),
    previousNetSales: MoneySchema.nullable(),
    tax: TaxMetricSchema.nullable(),
    refundRate: MetricRatioSchema.nullable(),
    wallet: WalletReportSchema,
    byVenue: z.array(RevenueReportVenueSchema),
})
    .openapi('RevenueReport', {
    description: 'The Reports screen’s money (F-ANL-03, spec/17 R-01, R-02): revenue by day, category and how it was paid, wallets and liabilities.',
});
export const SessionsReportFiguresSchema = z
    .object({
    sessions: z.int().min(0).nullable(),
    playSeconds: z.int().min(0).nullable(),
    averageSeconds: z.int().min(0).nullable(),
    activePlayers: z.int().min(0).nullable(),
    newPlayers: z.int().min(0).nullable(),
    inSessionSeconds: z.int().min(0),
    availableSeconds: z.int().min(0),
    /** M-10 in basis points; null with no station-seconds available. */
    occupancyBasisPoints: z.int().min(0).nullable(),
})
    .openapi('SessionsReportFigures', { description: 'M-06, M-07, M-09 and M-10 over a period; a figure with no data is null.' });
export const OccupancyCellSchema = z
    .object({
    /** 0 = Monday … 6 = Sunday: the weekday of the business day the hour belongs to. */
    weekday: z.int().min(0).max(6),
    /** The venue's clock hour, 0 … 23. */
    hour: z.int().min(0).max(23),
    inSessionSeconds: z.int().min(0),
    availableSeconds: z.int().min(0),
    basisPoints: z.int().min(0).nullable(),
})
    .openapi('OccupancyCell', { description: 'M-10 in one hour of one business weekday.' });
export const OccupancyHourSchema = z
    .object({ hour: z.int().min(0).max(23), inSessionSeconds: z.int().min(0), availableSeconds: z.int().min(0), basisPoints: z.int().min(0).nullable() })
    .openapi('OccupancyHour', { description: 'M-10 in one clock hour across every weekday.' });
export const OccupancyGroupSchema = z
    .object({
    /** A station class id or a zone id; null for stations in no zone. */
    id: z.string().nullable(),
    name: z.string().nullable(),
    stations: z.int().min(0),
    inSessionSeconds: z.int().min(0),
    availableSeconds: z.int().min(0),
    basisPoints: z.int().min(0).nullable(),
})
    .openapi('OccupancyGroup', { description: 'M-10 for one station class or zone.' });
export const SessionsReportDaySchema = z
    .object({ date: LocalDateSchema, sessions: z.int().min(0), playSeconds: z.int().min(0) })
    .openapi('SessionsReportDay', { description: 'Sessions filed under one business day and the play seconds they charged.' });
export const SessionsReportSchema = z
    .object({
    ...reportScopeShape,
    totals: SessionsReportFiguresSchema,
    previous: SessionsReportFiguresSchema,
    byDay: z.array(SessionsReportDaySchema),
    /** The hour a business day starts at (the cutoff's hour): lay the heatmap's hours out from it. */
    firstHour: z.int().min(0).max(23),
    /** 7 × 24 cells, Monday first, hour 0 first. */
    heatmap: z.array(OccupancyCellSchema),
    byHour: z.array(OccupancyHourSchema),
    /** The busiest cells, highest first (at most 3). */
    peaks: z.array(OccupancyCellSchema).max(3),
    byClass: z.array(OccupancyGroupSchema),
    byZone: z.array(OccupancyGroupSchema),
    /** False when a venue has no opening hours recorded: its whole range then counts as open. */
    hoursSet: z.boolean(),
})
    .openapi('SessionsReport', {
    description: 'Sessions and occupancy (F-ANL-03, spec/17 R-03): totals against the previous period, the hour × weekday heatmap, peaks, by class and zone.',
});
/** At most this many items on the Products tab. */
export const REPORTS_TOP_ITEMS = 20;
export const ProductsReportItemSchema = z
    .object({
    name: z.string(),
    /** The sale line's kind: `food`, `retail` or `other` (time lines are passes, below). */
    kind: z.enum(['food', 'retail', 'other']),
    quantity: z.int().min(0),
    /** Sold value ex-tax, after line discounts (its share of net sales). */
    net: MoneySchema,
    orders: z.int().min(0),
})
    .openapi('ProductsReportItem', { description: 'One food, drink or retail item’s sales over the range.' });
export const ProductsReportPassSchema = z
    .object({ name: z.string(), sold: z.int().min(0), granted: z.int().min(0), revenue: MoneySchema })
    .openapi('ProductsReportPass', { description: 'One pass’s holdings sold (with what they sold for) and granted over the range.' });
export const ProductsReportPlaySchema = z
    .object({ passes: MoneySchema, rateCard: MoneySchema, discounts: MoneySchema })
    .openapi('ProductsReportPlay', {
    description: 'Play sold, ex-tax, from the ledger: as passes, and as play time at the rate card (pay as you go, wallet, on account), and the play discounts.',
});
export const ProductsReportSchema = z
    .object({
    ...reportScopeShape,
    items: z.array(ProductsReportItemSchema).max(REPORTS_TOP_ITEMS),
    passes: z.array(ProductsReportPassSchema),
    /** Play sold, ex-tax, from the ledger: as passes, and as play time at the rate card (pay as you go, wallet, on account). */
    play: ProductsReportPlaySchema,
})
    .openapi('ProductsReport', { description: 'Products (F-ANL-03, spec/17 R-06): top food and drink, passes sold, play by passes and rate card.' });
export const StaffReportMemberSchema = z
    .object({
    /** Null for what no member did (the system, a player at the kiosk). */
    memberId: MemberIdSchema.nullable(),
    name: z.string().nullable(),
    /** M-01 of the entries they posted: what they took, wallet spends they rang up included. */
    revenue: MoneySchema,
    refunds: MoneySchema,
    topUps: MoneySchema,
    discounts: MoneySchema,
    voids: z.int().min(0),
    voidedValue: MoneySchema,
    comps: z.int().min(0),
    compSeconds: z.int().min(0),
})
    .openapi('StaffReportMember', { description: 'One member’s sales, refunds, top-ups, discounts, voids and comps over the range.' });
export const StaffReportSchema = z
    .object({ ...reportScopeShape, members: z.array(StaffReportMemberSchema) })
    .openapi('StaffReport', { description: 'Staff (F-ANL-03, spec/17 R-07, R-10): sales by member, refunds, voids, discounts and comps.' });
const reportResponses = {
    400: commonProblemResponses[400],
    401: commonProblemResponses[401],
    403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
    404: problemResponse('No such venue in this organisation, or not one you can see.', 'not-found'),
    429: commonProblemResponses[429],
    500: commonProblemResponses[500],
};
export const getReportRangeRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/reports/range',
    operationId: 'getReportRange',
    tags: ['reports'],
    summary: 'A named range’s business days at a venue',
    description: 'Resolves a preset (today, yesterday, last 7 or 30 days, this or last month) to business days in the venue’s ' +
        'timezone and cutoff, with the previous period it is compared with. Requires `reports:read`.',
    [PERMISSION_EXTENSION]: REPORTS_READ,
    request: { params: OrganizationScopeParamsSchema, query: ReportRangeQuerySchema },
    responses: { 200: jsonResponse(ReportRangeSchema, 'The range.'), ...reportResponses },
});
export const getRevenueReportRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/reports/revenue',
    operationId: 'getRevenueReport',
    tags: ['reports'],
    summary: 'Revenue: by day, category and how it was paid; wallets and liabilities',
    description: 'Revenue (M-01 — wallet spends count when spent, top-ups and bonus do not) and money in, against the previous ' +
        'period; by business day, by venue, by category (M-02) and by tender; wallet flows and what is owed. One venue ' +
        'or every venue you work at. At most 92 days. Requires `reports:financial`.',
    [PERMISSION_EXTENSION]: REPORTS_FINANCIAL,
    request: { params: OrganizationScopeParamsSchema, query: ReportsScopeQuerySchema },
    responses: { 200: jsonResponse(RevenueReportSchema, 'The report.'), ...reportResponses },
});
export const getSessionsReportRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/reports/sessions',
    operationId: 'getSessionsReport',
    tags: ['reports'],
    summary: 'Sessions and occupancy: the hour × weekday heatmap, peaks, by class and zone',
    description: 'Sessions, play time, players and occupancy (M-06 … M-10) against the previous period; occupancy by business ' +
        'weekday and clock hour, its peaks, and by station class and zone. One venue or every venue you work at. At most ' +
        '92 days. Requires `reports:read`.',
    [PERMISSION_EXTENSION]: REPORTS_READ,
    request: { params: OrganizationScopeParamsSchema, query: ReportsScopeQuerySchema },
    responses: { 200: jsonResponse(SessionsReportSchema, 'The report.'), ...reportResponses },
});
export const getProductsReportRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/reports/products',
    operationId: 'getProductsReport',
    tags: ['reports'],
    summary: 'Products: top food and drink, passes sold, play by passes and rate card',
    description: 'The best-selling food, drink and retail items (paid sales, ex-tax), passes sold and granted, and play sold as ' +
        'passes or as play time at the rate card. One venue or every venue you work at. At most 92 days. Requires ' +
        '`reports:financial`.',
    [PERMISSION_EXTENSION]: REPORTS_FINANCIAL,
    request: { params: OrganizationScopeParamsSchema, query: ReportsScopeQuerySchema },
    responses: { 200: jsonResponse(ProductsReportSchema, 'The report.'), ...reportResponses },
});
export const getStaffReportRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/reports/staff',
    operationId: 'getStaffReport',
    tags: ['reports'],
    summary: 'Staff: sales by member, refunds, voids, discounts and comps',
    description: 'Per member: the revenue of what they rang up (M-01 of the entries they posted), refunds, top-ups taken, ' +
        'discounts, sales voided and comps granted. One venue or every venue you work at. At most 92 days. Requires ' +
        '`reports:financial`.',
    [PERMISSION_EXTENSION]: REPORTS_FINANCIAL,
    request: { params: OrganizationScopeParamsSchema, query: ReportsScopeQuerySchema },
    responses: { 200: jsonResponse(StaffReportSchema, 'The report.'), ...reportResponses },
});
// ---------------------------------------------------------------------------------------------
// GET …/venues/{v}/floor/live — the floor's live figures (F-ANL-02)
// ---------------------------------------------------------------------------------------------
export const FloorLiveSchema = z
    .object({
    venueId: VenueIdSchema,
    /** The venue's business day now. */
    businessDay: LocalDateSchema,
    /** Takings today: M-01 for the business day so far — the figure the day's report shows. Null before the first payment. */
    takings: MoneySchema.nullable(),
    /** M-09: sessions filed under today. Null before the first. */
    sessionsToday: z.int().min(0).nullable(),
    /** Sessions running now, and on how many stations. */
    sessionsNow: z.int().min(0),
    stationsInSession: z.int().min(0),
    /** Stations offered: every station not disabled. */
    stationsOffered: z.int().min(0),
    /** Occupancy now (M-10's instant form): stations in session ÷ offered, basis points; null with none offered. */
    occupancyNowBasisPoints: z.int().min(0).nullable(),
    /** Calls for help open now (M-15). */
    openCalls: z.int().min(0),
    computedAt: IsoDateTimeSchema,
})
    .openapi('FloorLive', {
    description: 'The floor’s live figures (spec/13 §3, spec/17 §4.4; F-ANL-02): takings today by M-01, sessions, occupancy now and ' +
        'open calls — read from Postgres on every call, so a Valkey flush loses nothing and a late rollup never blanks it.',
});
export const getFloorLiveRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/floor/live',
    operationId: 'getFloorLive',
    tags: ['floor', 'reports'],
    summary: 'The floor’s live figures: takings today, sessions, occupancy',
    description: 'Takings today (M-01 for the venue’s business day so far, identical to the day’s report), sessions today and now, ' +
        'occupancy now and open calls (F-ANL-02). The floor re-reads it on `floor.session.started` / `ended`. Requires ' +
        '`shifts:day_close` — the floor shows takings "for a role that closes the day" (spec/13 §3).',
    [PERMISSION_EXTENSION]: SHIFTS_DAY_CLOSE,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(FloorLiveSchema, 'The live figures.'),
        401: commonProblemResponses[401],
        403: problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required'),
        404: problemResponse('No such venue in this organisation, or not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/hq/live — HQ Live (F-ANL-02, F-HQ-01)
// ---------------------------------------------------------------------------------------------
/** How long a venue's stations must all be offline, during its opening hours, to be flagged (spec/14 §2). */
export const HQ_LIVE_OFFLINE_MINUTES = 10;
export const HqLiveVenueSchema = z
    .object({
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    name: z.string(),
    sessionsNow: z.int().min(0),
    stationsTotal: z.int().min(0),
    stationsOnline: z.int().min(0),
    /** Since when every station has been offline (as HQ Live first saw it), or null while any is online. */
    offlineSince: IsoDateTimeSchema.nullable(),
})
    .openapi('HqLiveVenue', { description: 'One venue on HQ Live (spec/14 §2).' });
export const HqLiveSchema = z
    .object({
    /** When the read model was last rebuilt; null while it is being rebuilt (after a Valkey flush), with every figure null. */
    computedAt: IsoDateTimeSchema.nullable(),
    playersInSession: z.int().min(0).nullable(),
    stationsInUse: z.int().min(0).nullable(),
    stationsTotal: z.int().min(0).nullable(),
    venuesOnline: z.int().min(0).nullable(),
    venuesTotal: z.int().min(0).nullable(),
    organizationsOnline: z.int().min(0).nullable(),
    organizationsTotal: z.int().min(0).nullable(),
    /** Takings today: M-01 over each venue's own business day, summed per currency. */
    takingsToday: z.array(MoneySchema),
    /** M-07 at each organisation: players whose first session there started today (each venue's business day). */
    newPlayersToday: z.int().min(0).nullable(),
    /** Venues whose every station has been offline for more than 10 minutes during opening hours, longest first. */
    offlineVenues: z.array(HqLiveVenueSchema),
    /** The busiest venues now, most sessions first (at most 10). */
    busiestVenues: z.array(HqLiveVenueSchema).max(10),
})
    .openapi('HqLive', {
    description: 'HQ Live (spec/14 §2, spec/17 §4.4; F-ANL-02): players in session, stations in use, venues online, takings today ' +
        'and new players across the Platform, and the venues offline for more than 10 minutes during opening hours. A read ' +
        'model the worker rebuilds every minute from Postgres and presence; never a report.',
});
export const getHqLiveRoute = createRoute({
    method: 'get',
    path: '/v1/hq/live',
    operationId: 'getHqLive',
    tags: ['hq'],
    summary: 'HQ Live: the Platform right now',
    description: 'The live counters across every organisation (aggregate only — no organisation’s rows): players in session, stations ' +
        'in use, venues online, takings today (M-01) and new players, with the venues offline for more than 10 minutes ' +
        'during opening hours. Polled; the worker rebuilds it every minute. Platform scope: requires `hq:organizations`.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    responses: {
        200: jsonResponse(HqLiveSchema, 'The live counters.'),
        401: commonProblemResponses[401],
        403: problemResponse('Your platform role does not allow this.', 'forbidden', 'second-factor-required'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=reports.js.map